import { Actions as AUTHENTICATION_ACTIONS } from "@/redux/actions/authenticationActions";
import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import { Actions as GROUPACTIONS } from "@/redux/actions/groupActions";
import Tooltip from "@/tooltip";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { format } from "date-fns";
import { merge, setWith } from "lodash";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import Select from "react-select";
import { DateInputFormat } from "../../common/DateComponents";
import Spinner from "../../common/Spinner";

const General = ({
	updateCompanyDetails,
	currentOrg,
	settings,
	saving,
	disabled,
	jurisdictionOptions,
	jurisdiction,
	groupOptions,
	autoEnrolmentPostponeOptions,
	getGroups,
	groups,
	defaultPensionGroup,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);
		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });
		setData({ ...newData });
	};

	const [chosenDefaultGroup, setChosenDefaultGroup] = useState(defaultPensionGroup);
	const [autoEnrolmentDutiesStartDate, setAutoEnrolmentDutiesStartDate] = useState(
		settings?.autoEnrolmentDutiesStartDate || "",
	);
	const [lastReassessmentDate, setLastReassessmentDate] = useState(settings?.lastReassessmentDate || "");

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		updateCompanyDetails(
			cleanDeep(
				{
					org: String(currentOrg),
					...data,
					autoEnrolmentDutiesStartDate,
					lastReassessmentDate,
				},
				{
					emptyStrings: false,
					emptyArrays: false,
					emptyObjects: false,
				},
			),
		);
	};

	useEffect(() => {
		if (!settings.org) return;
		if (!groups?.length && jurisdiction == "UK") getGroups();

		setData({
			autoEnrolmentDutiesStartDate: new Date(settings?.autoEnrolmentDutiesStartDate),
			defaultPensionGroup: defaultPensionGroup,
			automaticPostponement: settings?.automaticPostponement,
			automaticEnrolment: settings?.automaticEnrolment,
			automaticEnrolmentLetter: settings?.automaticEnrolmentLetter,
		});
	}, [settings?.org, jurisdictionOptions?.length]);

	return (
		<>
			<form
				className="px-8"
				onSubmit={onSubmit}
			>
				<div className="form-section col-span-2 row-span-2">
					<div className="form-container col-xs-12">
						<h4>Pensions</h4>
					</div>
					<div className="form-container col-xs-12">
						<div className="col-xs-6">
							<div className="form-material form-container">
								<div className="form-material form-container">
									<label>
										<Tooltip content="This is the Duties Start Date provided by the pensions regulator if your business was employing staff before 2017 or the incorporation date of your company if later.">
											Auto Enrolment Duties Start Date
										</Tooltip>
									</label>
									<DatePicker
										isClearable={autoEnrolmentDutiesStartDate && !disabled}
										disabled={disabled}
										selected={
											(autoEnrolmentDutiesStartDate && new Date(autoEnrolmentDutiesStartDate)) ?? null
										}
										onChange={(date) =>
											setAutoEnrolmentDutiesStartDate(date ? format(date, "yyyy-MM-dd") : null)
										}
										maxDate={new Date()}
										dateFormat="do MMM yyyy"
										customInput={<DateInputFormat />}
									/>
								</div>
							</div>
						</div>
						<div className="col-xs-6">
							<div className="form-material form-container">
								<div className="form-material form-container">
									<label>
										<Tooltip content="This is the Duties Start Date provided by the pensions regulator if your business was employing staff before 2017 or the incorporation date of your company if later.">
											Last Reassessment Date
										</Tooltip>
									</label>
									<DatePicker
										isClearable={lastReassessmentDate && !disabled}
										disabled={disabled}
										selected={
											(lastReassessmentDate && new Date(lastReassessmentDate)) ?? null
										}
										onChange={(date) =>
											setLastReassessmentDate(date ? format(date, "yyyy-MM-dd") : null)
										}
										maxDate={new Date()}
										dateFormat="do MMM yyyy"
										customInput={<DateInputFormat />}
									/>
								</div>
							</div>
						</div>
						<div className="col-xs-6">
							<div className="form-material form-container">
								<label>Default Pension Group</label>
								<Select
									name="defaultPensionGroup"
									options={groupOptions}
									isDisabled={disabled}
									value={groupOptions?.find((i) => i.value == chosenDefaultGroup)}
									placeholder="Choose one"
									onChange={(e) => {
										setChosenDefaultGroup(e.value);
										updateField("defaultPensionGroup", e.value);
									}}
								/>
							</div>
						</div>

						<div className="col-xs-6">
							<div className="form-material form-container">
								<label>
									<Tooltip content="Automatically Postpone Auto Enrolment Pensions by 3 months for employees matching the selected option.">
										Automatically Postpone by 3 months
									</Tooltip>
								</label>
								<Select
									name="automaticPostponement"
									options={autoEnrolmentPostponeOptions}
									isDisabled={disabled}
									value={autoEnrolmentPostponeOptions?.find(
										(i) => i.value == data?.automaticPostponement,
									)}
									placeholder="Choose one"
									onChange={(e) => {
										updateField("automaticPostponement", e.value);
									}}
								/>
							</div>
						</div>
						{/* Option to Automatically Enrol (without prompt) anyone who has to be enroled using a checkbox radio button */}
						<div className="col-xs-8">
							<Tooltip content="Coming Soon">
								<div className="form-material form-container">
									<label className="css-input switch switch-sm switch-success">
										<input
											type="checkbox"
											className="checkbox-toggle"
											disabled={disabled}
											checked={!!data?.automaticEnrolment}
											onChange={(e) => {}}
											onClick={(e) => {
												updateField("automaticEnrolment", !data.automaticEnrolment ? 1 : 0);
											}}
										/>
										<span />
										Automatically Enrol employees into the {data?.defaultPensionGroup?.name} pension
										scheme if they meet the Auto Enrolment criteria.
									</label>
								</div>
							</Tooltip>
						</div>

						<div className="col-xs-8">
							<Tooltip content="Coming Soon">
								<div className="form-material form-container">
									<label className="css-input switch switch-sm switch-success">
										<input
											type="checkbox"
											className="checkbox-toggle"
											disabled={true || disabled}
											checked={!!data?.automaticEnrolmentLetter}
											onChange={(e) => {}}
											onClick={(e) => {
												updateField(
													"automaticEnrolmentLetter",
													!data.automaticEnrolmentLetter ? 1 : 0,
												);
											}}
										/>
										<span />
										Automatically send Auto Enrolment Letters upon generation.
									</label>
								</div>
							</Tooltip>
						</div>
					</div>
					<div className="clearfix" />
				</div>
				<div className="col-span-3">
					{!disabled && (
						<div className="text-right">
							<button
								className="btn btn-success"
								type="submit"
							>
								{saving ? (
									<Spinner
										style={{ height: "1.5em", float: "left", marginRight: 5, float: "left" }}
										className="animate-spin -ml-1 mr-3 h-5 w-5"
									/>
								) : (
									<CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								)}
								<div style={{ float: "left" }}>{saving ? "Saving..." : "Save"}</div>
							</button>
						</div>
					)}
					<div className="clearfix" />
				</div>
				<div className="clearfix" />
			</form>
		</>
	);
};

const mapStateToProps = (state) => {
	const groupOptions = state?.pensions?.group?.list?.map((group) => ({
		value: group.id,
		label: group.name,
	}));

	return {
		isAdmin: state.my?.profile?.isAdmin,
		profile: state?.my?.profile,
		hasMFA: state?.my?.profile?.hasMFA,
		settings: state.company?.settings,
		currentOrg: state.company?.settings?.org,
		jurisdictionOptions: (state.jurisdictions || [])?.map(({ countryCode, name }) => ({
			label: `${name}/${countryCode}`,
			value: countryCode,
		})),
		disabled:
			state?.permissions?.my?.permissions?.settings == "Read" || state?.company?.settings?.status == "Archived",
		saving: state.company?.saving,
		jurisdiction: state.company?.settings?.jurisdiction,
		companyAdvisor: state.company?.advisor,
		isEnterprise: state.company?.advisor?.bureau === "Enterprise",
		clientCanReview: state.permissions?.plan?.clientReview,
		groupOptions,
		autoEnrolmentPostponeOptions: [
			{
				value: "none",
				label: "Do not Auto Postpone",
			},
			{
				value: "all",
				label: "All Employees",
			},
			{
				value: "casual",
				label: "Employees who have variable pay",
			},
		],
		groups: state?.pensions?.group?.list || [],
		defaultPensionGroup:
			state.company?.settings?.defaultPensionGroup ||
			(groupOptions && groupOptions.length > 0 ? groupOptions[0].value : null),
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateCompanyDetails: (body) => dispatch(COMPANY_ACTIONS.updateCompanyDetails(body)),
	getGroups: (options) => dispatch(GROUPACTIONS.getGroups(options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(General);
